import { useId } from "react";
import {
    DEFAULT_BOT,
    defaultParams,
    describeBotParams,
    sameParams,
    setBotParam,
    validParam,
    type BotChoice,
    type BotParamSpec,
    type BotParams,
    type BotTypeDescriptor,
} from "./botTypes";

/** True when every parameter of a non-default choice is acceptable (the default bot has none). */
export function botChoiceValid(types: BotTypeDescriptor[], choice: BotChoice | null): boolean {
    if (!choice) return true;
    const bot = types.find((t) => t.id === choice.type && t.version === choice.version);
    return !!bot && bot.params.every((p) => validParam(p, choice.params[p.id]));
}

/** One parameter control: integers are a range plus a number box, booleans a checkbox, choices a select. */
function ParamControl({ spec, value, onChange }: { spec: BotParamSpec; value: unknown; onChange: (value: number | boolean | string) => void }) {
    const id = useId();
    const invalid = !validParam(spec, value);
    const help = spec.help ? <p className="guild-hint" id={`${id}-help`}>{spec.help}</p> : null;
    if (spec.kind === "integer")
        return (
            <div className="guild-bot-param">
                <label htmlFor={`${id}-number`}>{spec.label}{spec.unit ? ` (${spec.unit})` : ""}</label>
                <div className="guild-bot-range">
                    <input
                        type="range"
                        aria-label={`${spec.label} slider`}
                        min={spec.min}
                        max={spec.max}
                        step={spec.step ?? 1}
                        value={typeof value === "number" && Number.isFinite(value) ? value : Number(spec.default)}
                        onChange={(e) => onChange(Number(e.target.value))}
                    />
                    <input
                        id={`${id}-number`}
                        type="number"
                        min={spec.min}
                        max={spec.max}
                        step={spec.step ?? 1}
                        value={typeof value === "number" && Number.isFinite(value) ? value : ""}
                        aria-invalid={invalid}
                        aria-describedby={[invalid ? `${id}-error` : "", spec.help ? `${id}-help` : ""].filter(Boolean).join(" ") || undefined}
                        // An emptied box is an invalid value (NaN), not a no-op: the player sees the error and can type a new number.
                        onChange={(e) => onChange(e.target.value === "" ? Number.NaN : Number(e.target.value))}
                    />
                </div>
                {invalid && <p role="alert" id={`${id}-error`} className="guild-bot-error">Enter a whole number from {spec.min} to {spec.max}{spec.step && spec.step > 1 ? ` in steps of ${spec.step}` : ""}.</p>}
                {help}
            </div>
        );
    if (spec.kind === "boolean")
        return (
            <div className="guild-bot-param">
                <label className="guild-toggle">
                    <span>{spec.label}</span>
                    <input type="checkbox" checked={value === true} aria-describedby={spec.help ? `${id}-help` : undefined} onChange={(e) => onChange(e.target.checked)} />
                </label>
                {help}
            </div>
        );
    return (
        <div className="guild-bot-param">
            <label htmlFor={`${id}-select`}>{spec.label}</label>
            <select id={`${id}-select`} value={String(value ?? "")} aria-describedby={spec.help ? `${id}-help` : undefined} onChange={(e) => onChange(e.target.value)}>
                {spec.options?.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
            {help}
        </div>
    );
}

/** The parameter form of one bot type, with "Reset to defaults". */
export function BotParamForm({ bot, params, onChange }: { bot: BotTypeDescriptor; params: BotParams; onChange: (params: BotParams) => void }) {
    if (bot.params.length === 0) return null;
    const atDefaults = sameParams(bot, params, defaultParams(bot));
    return (
        <div className="guild-bot-params" role="group" aria-label={`${bot.label} settings`}>
            {bot.params.map((spec) => (
                <ParamControl key={spec.id} spec={spec} value={params[spec.id]} onChange={(value) => onChange(setBotParam(bot, params, spec.id, value))} />
            ))}
            <button type="button" className="guild-link guild-bot-reset" disabled={atDefaults} onClick={() => onChange(defaultParams(bot))}>
                Reset to defaults
            </button>
        </div>
    );
}

/**
 * The bot type picker with its parameter form. `choice` is null for the house (default) bot; any
 * other value is a type of `types` with its parameters. Rendering nothing when a game offers one bot.
 */
export function BotTypeFields({ types, choice, onChange, legend = "Bot type" }: { types: BotTypeDescriptor[]; choice: BotChoice | null; onChange: (choice: BotChoice | null) => void; legend?: string }) {
    const group = useId();
    if (types.length < 2) return null;
    const selected = choice ? types.find((t) => t.id === choice.type) : undefined;
    return (
        <fieldset className="guild-bot-types">
            <legend>{legend}</legend>
            {types.map((type) => {
                const checked = type.id === DEFAULT_BOT ? !choice : choice?.type === type.id;
                return (
                    <label key={type.id} className="guild-bot-type">
                        <input
                            type="radio"
                            name={group}
                            checked={checked}
                            onChange={() => onChange(type.id === DEFAULT_BOT ? null : { type: type.id, version: type.version, params: defaultParams(type) })}
                        />
                        <span>
                            <strong>{type.label}</strong>
                            <small>{type.summary}</small>
                        </span>
                    </label>
                );
            })}
            {choice && selected && <BotParamForm bot={selected} params={choice.params} onChange={(params) => onChange({ ...choice, params })} />}
            {choice && selected && <p className="guild-hint">{describeBotParams(selected, choice.params).join(" · ")}</p>}
        </fieldset>
    );
}
